import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, getQueryFn, invalidateSiteAuditQueries } from "@/lib/queryClient";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, RefreshCw, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { userHasPermission } from "@/lib/permissions";
import { apiErrorMessage, isSiteAuditInProgress } from "@/lib/site-audit";
import type { UnsupportedFigure } from "@shared/site-audit-grounding";
import type { WpStaffSection } from "@shared/site-audit-wordpress";
import { presentAuditReport } from "@shared/site-audit-report-view";
import { AuditReport } from "@/components/site-audit/report/audit-report";
import { SecurityCoverage } from "@/components/site-audit/report/security-coverage";
import { ShareLinkCard } from "@/components/site-audit/report/share-link-card";
import { useBranding } from "@/hooks/useBranding";

type AuditStatus =
  | "queued"
  | "collecting_dataforseo"
  | "collecting_pagespeed"
  | "collecting_moz"
  | "analyzing"
  | "complete"
  | "failed";

interface AuditHistoryRun {
  id: string;
  status: AuditStatus;
  createdAt: string;
  rerunOfJobId: string | null;
  durationMs: number | null;
  current: boolean;
}

interface SiteAuditJob {
  id: string;
  domain: string;
  status: AuditStatus;
  createdAt: string;
  updatedAt: string;
  competitors: string[] | null;
  brief: string | null;
  error: string | null;
  failedStep: string | null;
  report: AuditReport | null;
  normalizedData: unknown | null;
  costEstimate: number | null;
  costBreakdown: {
    totalUsd?: number;
    lines?: {
      provider: string;
      operation: string;
      usd: number;
      cached: boolean;
      rows?: number;
      inputTokens?: number;
      outputTokens?: number;
    }[];
  } | null;
  durationMs: number | null;
  isLatest: boolean;
  history: AuditHistoryRun[];
  wordpress: WpStaffSection | null;
}

interface AuditReport {
  executiveSummary: string;
  keyFindings: {
    title: string;
    detail: string;
    severity: "critical" | "high" | "medium" | "low";
    category: string;
  }[];
  competitiveAnalysis: string;
  quickWins: { action: string; impact: string; effort: "low" | "medium" }[];
  recommendations: {
    priority: "P0" | "P1" | "P2" | "P3";
    finding: string;
    opportunity: string;
    action: string;
    impact: "high" | "medium" | "low";
  }[];
  nextSteps: string;
  grounding?: { unsupported: UnsupportedFigure[] };
}

const STEPS: { key: AuditStatus; label: string }[] = [
  { key: "queued", label: "Queued" },
  { key: "collecting_dataforseo", label: "DataForSEO" },
  { key: "collecting_pagespeed", label: "PageSpeed" },
  { key: "collecting_moz", label: "Moz" },
  { key: "analyzing", label: "Analyzing" },
  { key: "complete", label: "Complete" },
];

const STATUS_ORDER = STEPS.map((s) => s.key);

function StepIndicator({ current }: { current: AuditStatus }) {
  const isFailed = current === "failed";
  const currentIdx = STATUS_ORDER.indexOf(current);

  return (
    <ol className="flex items-center gap-2 flex-wrap">
      {STEPS.map((step, i) => {
        const done = currentIdx > i || current === "complete";
        const active = currentIdx === i && !isFailed;
        return (
          <li key={step.key} className="flex items-center gap-2">
            {i > 0 && <span className="text-muted-foreground text-xs">›</span>}
            <span
              className={`text-sm font-medium ${
                done
                  ? "text-emerald-600"
                  : active
                  ? "text-primary"
                  : "text-muted-foreground"
              }`}
            >
              {step.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function WordPressSection({
  jobId,
  canPaste,
}: {
  jobId: string;
  canPaste: boolean;
}) {
  const { toast } = useToast();
  const [paste, setPaste] = useState("");
  const pasteMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/site-audit/${jobId}/site-health`, { paste });
      return response.json();
    },
    onSuccess: async () => {
      setPaste("");
      await invalidateSiteAuditQueries();
      toast({ title: "Site Health report attached" });
    },
    onError: (error) => {
      toast({
        title: "Could not read that report",
        description: apiErrorMessage(error, "Paste the Site Health report again."),
        variant: "destructive",
      });
    },
  });
  if (!canPaste) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Site Health report</CardTitle>
      </CardHeader>
      <CardContent>
          <form
            className="space-y-2"
            onSubmit={(event) => {
              event.preventDefault();
              pasteMutation.mutate();
            }}
          >
            <Textarea
              value={paste}
              onChange={(event) => setPaste(event.target.value)}
              placeholder="Paste the report from Tools, Site Health, Info."
              rows={6}
            />
            <Button type="submit" variant="outline" disabled={!paste.trim() || pasteMutation.isPending}>
              {pasteMutation.isPending ? "Attaching…" : "Attach Site Health"}
            </Button>
          </form>
      </CardContent>
    </Card>
  );
}

export default function SiteAuditDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { brandName, logoUrl } = useBranding();
  const canCreate = userHasPermission(user?.effectivePermissions, "site_audits.create");

  const { data: job, isLoading } = useQuery<SiteAuditJob>({
    queryKey: [`/api/site-audit/${id}`],
    queryFn: getQueryFn({ on401: "throw" }),
    refetchInterval: (query) => {
      const data = query.state.data as SiteAuditJob | undefined;
      if (!data) return false;
      const familyBusy = (data.history ?? []).some((run) => isSiteAuditInProgress(run.status));
      if (familyBusy || isSiteAuditInProgress(data.status)) return 8_000;
      return false;
    },
  });

  const rerunMutation = useMutation({
    mutationFn: async (input: { audit: SiteAuditJob; refreshPaid: boolean }) => {
      const query = input.refreshPaid ? "?refreshPaid=true" : "";
      const response = await apiRequest("POST", `/api/site-audit/${input.audit.id}/rerun${query}`);
      return (await response.json()) as { job: { id: string } };
    },
    onSuccess: async (body, input) => {
      await invalidateSiteAuditQueries();
      toast({
        title: input.refreshPaid ? "Paid data refresh queued" : "Audit re-run queued",
        description: `A new run for ${input.audit.domain} has started.`,
      });
      navigate(`/site-audits/${body.job.id}`);
    },
    onError: (error) => {
      toast({
        title: "Could not re-run audit",
        description: apiErrorMessage(error, "Failed to re-run audit."),
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <div className="p-6 flex items-center gap-2 text-muted-foreground">
        <RefreshCw className="h-4 w-4 animate-spin" />
        Loading…
      </div>
    );
  }

  if (!job) {
    return <div className="p-6 text-muted-foreground">Audit not found.</div>;
  }

  const report = job.report;
  const isRunning = !["complete", "failed"].includes(job.status);
  const familyBusy = (job.history ?? []).some((run) => isSiteAuditInProgress(run.status));
  const rerunBlocked = familyBusy || rerunMutation.isPending;

  return (
    <div>
      <PageHeader
        title={job.domain}
        description={`Site audit · Started ${new Date(job.createdAt).toLocaleString()}`}
        action={
          <div className="flex flex-wrap gap-2">
            {canCreate && job.isLatest && (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (
                      confirm(
                        `Refresh paid data for ${job.domain}? This pays for DataForSEO, Moz, and Claude again. The previous run stays in history.`,
                      )
                    ) {
                      rerunMutation.mutate({ audit: job, refreshPaid: true });
                    }
                  }}
                  disabled={rerunBlocked}
                >
                  Refresh paid data
                </Button>
                <Button
                  onClick={() => {
                    if (
                      confirm(
                        `Re-run the audit for ${job.domain}? Saved DataForSEO and Moz results from the last 7 days are reused, and the crawl from the last 2 days. This run stays in history.`,
                      )
                    ) {
                      rerunMutation.mutate({ audit: job, refreshPaid: false });
                    }
                  }}
                  disabled={rerunBlocked}
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${rerunMutation.isPending ? "animate-spin" : ""}`} />
                  {rerunMutation.isPending ? "Starting…" : "Re-run audit"}
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => navigate("/site-audits")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              All Audits
            </Button>
          </div>
        }
      />

      <div className="p-6 space-y-6">
        {/* Status stepper */}
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <StepIndicator current={job.status} />
              {isRunning && (
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  Running…
                </div>
              )}
              {job.status === "complete" && (
                <div className="flex items-center gap-1.5 text-sm text-emerald-600">
                  <CheckCircle2 className="h-4 w-4" />
                  {job.durationMs ? `Completed in ${Math.round(job.durationMs / 1000)}s` : "Complete"}
                </div>
              )}
              {job.status === "failed" && (
                <div className="flex items-center gap-1.5 text-sm text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                  Failed{job.failedStep ? ` at ${job.failedStep}` : ""}
                </div>
              )}
            </div>

            {job.status === "failed" && job.error && (
              <p className="mt-3 rounded border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {job.error}
              </p>
            )}

            {isRunning && (
              <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                This audit takes 3–5 minutes. The page refreshes automatically.
              </div>
            )}
            {canCreate && job.isLatest && familyBusy && (
              <p className="mt-3 text-sm text-muted-foreground">
                A re-run is already queued or running.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Run history</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(job.history ?? []).map((run) => (
              <div key={run.id} className="flex items-center justify-between gap-3">
                {run.current ? (
                  <span className="text-sm font-medium">
                    {new Date(run.createdAt).toLocaleString()}
                  </span>
                ) : (
                  <button
                    className="text-sm text-primary hover:underline text-left"
                    onClick={() => navigate(`/site-audits/${run.id}`)}
                  >
                    {new Date(run.createdAt).toLocaleString()}
                  </button>
                )}
                <div className="flex items-center gap-2">
                  {run.durationMs != null && (
                    <span className="text-xs text-muted-foreground">
                      {Math.round(run.durationMs / 1000)}s
                    </span>
                  )}
                  {run.current && <Badge variant="outline">Current</Badge>}
                  <Badge variant={run.status === "failed" ? "destructive" : "secondary"}>
                    {run.status === "complete"
                      ? "Complete"
                      : run.status === "failed"
                      ? "Failed"
                      : run.status === "queued"
                      ? "Queued"
                      : "Running"}
                  </Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Brief + metadata */}
        {(job.brief || (job.competitors && job.competitors.length > 0)) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Audit Inputs</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {job.competitors && job.competitors.length > 0 && (
                <div>
                  <p className="text-xs uppercase font-semibold text-muted-foreground mb-1">Competitors</p>
                  <div className="flex flex-wrap gap-1.5">
                    {job.competitors.map((c) => (
                      <Badge key={c} variant="secondary">{c}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {job.brief && (
                <div>
                  <p className="text-xs uppercase font-semibold text-muted-foreground mb-1">Brief</p>
                  <p className="text-sm text-muted-foreground">{job.brief}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {job.normalizedData == null && report == null && job.wordpress && job.wordpress.state !== "missing" ? (
          <SecurityCoverage
            security={presentAuditReport({
              domain: job.domain,
              wordpress: job.wordpress,
              mode: "staff",
              jobComplete: !isRunning,
            }).security}
          />
        ) : null}

        {(job.normalizedData != null || report) && (
          <>
            {(report?.grounding?.unsupported.length ?? 0) > 0 && (
              <p className="rounded border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-foreground">
                Highlighted figures are not in the source data. The sentences are unchanged.
              </p>
            )}
            <AuditReport
              report={presentAuditReport({
                domain: job.domain,
                normalizedData: job.normalizedData,
                report,
                wordpress: job.wordpress,
                mode: "staff",
                jobComplete: !isRunning,
              })}
              brandName={brandName}
              logoUrl={logoUrl}
              dateLabel={formatAuditDate(textCollectedAt(job.normalizedData) ?? job.createdAt)}
              includeEvidence
              includeSecurity
            />
            {job.costEstimate != null && (
              <div className="space-y-1 text-right text-xs text-muted-foreground">
                <p>Estimated API cost: ${job.costEstimate.toFixed(4)}</p>
                {(job.costBreakdown?.lines ?? []).map((line) => (
                  <p key={`${line.provider}-${line.operation}`}>
                    {line.provider} {line.operation}: {line.cached ? "cached" : `$${line.usd.toFixed(4)}`}
                    {line.rows != null ? ` · ${line.rows} rows` : ""}
                    {line.inputTokens != null ? ` · ${line.inputTokens} in / ${line.outputTokens ?? 0} out` : ""}
                  </p>
                ))}
              </div>
            )}
          </>
        )}

        {canCreate ? <ShareLinkCard jobId={job.id} /> : null}
        <WordPressSection jobId={job.id} canPaste={canCreate} />
      </div>
    </div>
  );
}

function textCollectedAt(data: unknown): string | null {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const value = (data as { collectedAt?: unknown }).collectedAt;
  return typeof value === "string" && value.length > 0 ? value : null;
}

function formatAuditDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}
